import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch
} from 'vue'
import { useDebounceFn } from '@vueuse/core'
import localforage from 'localforage'
import { useAuth } from '@/composables/useAuth'
import { useSite } from '@/composables/useSite'
import { emitter } from '@/plugins/emitter'

type HostVm = {
  $message: (opts: Record<string, unknown>) => void
}

type MailLoginForm = {
  resetFields: () => void
  validate: (cb: (valid: boolean) => void) => void
}

type BasicData = { email?: string }


export default /*#__PURE__*/_defineComponent({
  __name: 'LoginSpecialForms',
  props: {
  isPlainButton: { type: Boolean, default: false }
},
  setup(__props) {

const SING_IN_MAIL_SEND = 'sing-in-mail-send'
/** authMethods は `sing-in-mail-fail` を emit（旧購読名 failed との差は forward で吸収） */
const SING_IN_MAIL_FAIL = 'sing-in-mail-fail'



const vm = getCurrentInstance()!.proxy as unknown as HostVm
const { getClient } = useAuth()
const { appFlag } = useSite()

const loginEmailSending = ref(false)
const isAgree = ref(true)
const showMailLink = ref(false)
const emailExists = ref<boolean | null>(null)
const mailLoginForm = ref<MailLoginForm | null>(null)
const mailLogin = reactive({
  email: '',
  passwd: '',
  passwdConfirm: ''
})

const mailLoginRules = {
  email: [
    {
      required: true,
      message: 'メールアドレスをいれてね',
      trigger: 'blur'
    },
    {
      required: true,
      type: 'email',
      message: 'メールアドレスをいれてね',
      trigger: 'blur'
    }
  ]
}

const submitButtonLabel = computed(() => {
  if (emailExists.value === true) return 'ログインする'
  if (emailExists.value === false) return '会員登録する'
  return 'ログイン・会員登録する'
})

function applyBasicDataEmail(payload: unknown) {
  if (payload == null || typeof payload !== 'object') return
  const email = (payload as BasicData).email
  if (email && !mailLogin.email) {
    mailLogin.email = email
  }
}

function isInValidEmail(mailStr: string) {
  const reg =
    /^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/
  return !reg.test(mailStr)
}

function isValidPassword(password: string) {
  if (password.length < 8 || password.length > 20) return false
  const hasLowerCase = /[a-z]/.test(password)
  const hasUpperCase = /[A-Z]/.test(password)
  const hasNumber = /\d/.test(password)
  return hasLowerCase && hasUpperCase && hasNumber
}

const checkEmailExistsDebounced = useDebounceFn(async (email: string) => {
  const client = getClient()
  if (!client?.checkEmailExists) return
  const exists = await client.checkEmailExists(email)
  if (mailLogin.email === email) {
    emailExists.value = exists
  }
}, 400)

watch(
  () => mailLogin.email,
  (email) => {
    emailExists.value = null
    if (isInValidEmail(email)) return
    checkEmailExistsDebounced(email)
  }
)

function requireAgree() {
  if (isAgree.value) return true
  vm.$message({
    message: '利用規約と個人情報の扱いに同意してください。',
    type: 'error'
  })
  return false
}

async function submitIdPw() {
  if (!requireAgree()) return
  if (isInValidEmail(mailLogin.email)) {
    vm.$message({
      message: 'メールアドレスを正しく入力してください',
      type: 'error'
    })
    return
  }
  if (!mailLogin.passwd) {
    vm.$message({
      message: 'パスワードを入力してください',
      type: 'error'
    })
    return
  }

  const client = getClient()
  if (!client) {
    vm.$message({
      message: 'エラーが発生しました。もう一度お試しください。',
      type: 'error'
    })
    return
  }

  loginEmailSending.value = true
  try {
    const signInResult = await client.signInEmailPasswd(
      mailLogin.email,
      mailLogin.passwd,
      { signupFallback: true }
    )
    if (signInResult !== 'retry-as-signup') return

    if (emailExists.value === true) {
      vm.$message({
        message:
          'ログインに失敗しました。メールアドレスまたはパスワードが違います',
        type: 'error'
      })
      return
    }
    if (mailLogin.passwd !== mailLogin.passwdConfirm) {
      vm.$message({
        message: 'パスワードと確認用パスワードが一致していません。',
        type: 'error'
      })
      return
    }
    if (!isValidPassword(mailLogin.passwd)) {
      vm.$message({
        message:
          'パスワードを正しい形式で入力してください。※半角英小文字・大文字・数字を組合せて8〜20字で入力してください。記号も使用できます。',
        type: 'error'
      })
      return
    }
    await client.signOnEmailPasswd(mailLogin.email, mailLogin.passwd, {
      signInAlreadyTried: true
    })
  } catch (error) {
    console.error(error)
    vm.$message({
      message: 'エラーが発生しました。もう一度お試しください。',
      type: 'error'
    })
  } finally {
    loginEmailSending.value = false
  }
}

function signInGoogle() {
  if (!requireAgree()) return
  getClient()?.signInGoogle()
}

function signInLine() {
  if (!requireAgree()) return
  getClient()?.signInLine()
}

function signInApple() {
  if (!requireAgree()) return
  getClient()?.signInApple()
}

function resetloginEmail() {
  mailLogin.email = ''
  loginEmailSending.value = false
  mailLoginForm.value?.resetFields()
}

function mailSubmit() {
  if (!requireAgree()) return
  mailLoginForm.value?.validate((valid) => {
    if (!valid) return
    getClient()?.signInEmail(mailLogin.email)
    loginEmailSending.value = true
  })
}

onMounted(() => {
  emitter.on(SING_IN_MAIL_SEND, resetloginEmail)
  emitter.on(SING_IN_MAIL_FAIL, resetloginEmail)
  emitter.on('basic-data-changed', applyBasicDataEmail)
  localforage.getItem<BasicData>('basicData').then((data) => {
    applyBasicDataEmail(data)
  })
})

onBeforeUnmount(() => {
  emitter.off(SING_IN_MAIL_SEND, resetloginEmail)
  emitter.off(SING_IN_MAIL_FAIL, resetloginEmail)
  emitter.off('basic-data-changed', applyBasicDataEmail)
})

return { __sfc: true,SING_IN_MAIL_SEND, SING_IN_MAIL_FAIL, vm, getClient, appFlag, loginEmailSending, isAgree, showMailLink, emailExists, mailLoginForm, mailLogin, mailLoginRules, submitButtonLabel, applyBasicDataEmail, isInValidEmail, isValidPassword, checkEmailExistsDebounced, requireAgree, submitIdPw, signInGoogle, signInLine, signInApple, resetloginEmail, mailSubmit }
}

})